import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { filter, from } from 'rxjs';
import { AuthService } from '@/core/auth/auth.service';
import { RelatorioSelecaoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/relatorio-selecao-pj/relatorio-selecao-pj.service';
import { RelatorioSelecaoPjJobService } from '@/core/services/relatorio-selecao-pj-job.service';
import {
    PrestadorListItem,
    PrestadorRefRequest,
    RelatorioSelecaoPjExportResponse,
    RelatorioSelecaoPjJobCreatedResponse,
    RelatorioSelecaoPjLoteExportRequest
} from '@/core/api/sicom-api/registro/prestador/relatorios/relatorio-selecao-pj/relatorio-selecao-pj.model';
import { SelecionarPrestadores } from './selecionar-prestadores/selecionar-prestadores';

export interface ReportField {
    id: string;
    label: string;
    icon: string;
    category: string;
}

const ALL_FIELDS: ReportField[] = [
    { id: 'cnpj', label: 'CNPJ', icon: 'pi pi-id-card', category: 'Identificação' },
    { id: 'nome', label: 'Nome / Razão Social', icon: 'pi pi-building', category: 'Identificação' },
    { id: 'situacao', label: 'Situação', icon: 'pi pi-circle-fill', category: 'Situação' },
    { id: 'classificacao', label: 'Classificação', icon: 'pi pi-tag', category: 'Situação' },
    { id: 'diretoria', label: 'Diretoria', icon: 'pi pi-users', category: 'Seções' },
    { id: 'corpoSocietario', label: 'Corpo Societário', icon: 'pi pi-sitemap', category: 'Seções' },
    { id: 'servicos', label: 'Serviços', icon: 'pi pi-briefcase', category: 'Seções' },
    { id: 'enderecos', label: 'Endereços e Telefones', icon: 'pi pi-map-marker', category: 'Seções' }
];

/**
 * Tela de configuração e geração de relatório personalizado por seleção de prestadores.
 * Orquestra a fila de análise, a configuração de campos e o disparo do fluxo assíncrono de
 * exportação PDF via Jasper (mesmo padrão do Relatório por Seleção de Médicos) e download de CSV.
 */
@Component({
    selector: 'app-relatorio-selecao-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, TitleCasePipe, FormsModule, Button, TableModule, ToggleSwitch, RelatorioContainer, SelecionarPrestadores],
    templateUrl: './relatorio-selecao-pj.html',
    styleUrl: './relatorio-selecao-pj.scss'
})
export class RelatorioSelecaoPj implements OnInit {
    private readonly relatorioService = inject(RelatorioSelecaoPjService);
    private readonly authService = inject(AuthService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);
    readonly jobService = inject(RelatorioSelecaoPjJobService);

    readonly job$ = this.jobService.job$;
    readonly ALL_FIELDS = ALL_FIELDS;

    /** Controla a abertura do modal de busca e seleção de prestadores. */
    readonly modalVisible = signal(false);

    readonly selectedPrestadores = signal<PrestadorListItem[]>([]);

    /** Ordem de exibição de todos os campos — determina a ordem das colunas no relatório. */
    readonly fieldOrder = signal<ReportField[]>([...ALL_FIELDS]);
    private readonly selectedIds = signal<Set<string>>(new Set(ALL_FIELDS.slice(0, 3).map((f) => f.id)));
    readonly selectedFields = computed(() => this.fieldOrder().filter((f) => this.selectedIds().has(f.id)));
    readonly availableFields = computed(() => this.fieldOrder().filter((f) => !this.selectedIds().has(f.id)));

    readonly isLoading = signal(false);
    readonly pdfBlobs = signal<Blob[]>([]);
    readonly camposExpandidos = signal(false);

    private readonly usuarioNome = signal('');

    get isPending(): boolean {
        return this.jobService.job?.status === 'PENDING';
    }

    constructor() {
        from(this.authService.getUserInfo())
            .pipe(takeUntilDestroyed())
            .subscribe({ next: (u) => this.usuarioNome.set(u.name || ''), error: () => {} });

        // acumula blobs de jobs assíncronos (202) quando o polling termina
        this.jobService.job$
            .pipe(
                takeUntilDestroyed(),
                filter((job) => job?.status === 'DONE' && !!job.pdfBlob)
            )
            .subscribe((job) => this.pdfBlobs.update((list) => [...list, job!.pdfBlob!]));
    }

    ngOnInit(): void {
        this.jobService.restaurarDeSessionStorage();
    }

    // ── Modal de seleção ──────────────────────────────────────────────────────
    abrirModal(): void {
        this.modalVisible.set(true);
    }

    /** Substitui a fila com a seleção confirmada pelo modal. */
    onPrestadoresConfirmados(prestadores: PrestadorListItem[]): void {
        this.selectedPrestadores.set(prestadores);
    }

    // ── Fila de análise (chave composta nuCrm+sgUf, igual ao médico) ──────────
    isPrestadorSelected(p: PrestadorListItem): boolean {
        return this.selectedPrestadores().some((s) => s.nuCrm === p.nuCrm && s.sgUf === p.sgUf);
    }

    togglePrestador(p: PrestadorListItem): void {
        if (this.isPrestadorSelected(p)) {
            this.selectedPrestadores.update((list) => list.filter((s) => !(s.nuCrm === p.nuCrm && s.sgUf === p.sgUf)));
        } else {
            this.selectedPrestadores.update((list) => [...list, p]);
        }
    }

    removerPrestador(p: PrestadorListItem): void {
        this.selectedPrestadores.update((list) => list.filter((s) => !(s.nuCrm === p.nuCrm && s.sgUf === p.sgUf)));
    }

    desmarcarTodos(): void {
        this.selectedPrestadores.set([]);
    }

    // ── Campos do relatório ───────────────────────────────────────────────────
    toggleField(field: ReportField): void {
        this.selectedIds.update((s) => {
            const next = new Set(s);
            if (next.has(field.id)) next.delete(field.id);
            else next.add(field.id);
            return next;
        });
    }

    onFieldToggle(field: ReportField, checked: boolean): void {
        this.selectedIds.update((s) => {
            const next = new Set(s);
            if (checked) next.add(field.id);
            else next.delete(field.id);
            return next;
        });
    }

    positionInReport(fieldId: string): number {
        return this.selectedFields().findIndex((f) => f.id === fieldId) + 1;
    }

    hasField(id: string): boolean {
        return this.selectedIds().has(id);
    }

    addField(field: ReportField): void {
        this.selectedIds.update((s) => {
            const n = new Set(s);
            n.add(field.id);
            return n;
        });
    }

    removeField(fieldOrId: ReportField | string): void {
        const id = typeof fieldOrId === 'string' ? fieldOrId : fieldOrId.id;
        this.selectedIds.update((s) => {
            const n = new Set(s);
            n.delete(id);
            return n;
        });
    }

    moveUp(index: number): void {
        if (index === 0) return;
        this.fieldOrder.update((arr) => {
            const next = [...arr];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next;
        });
    }

    moveDown(index: number): void {
        if (index >= this.fieldOrder().length - 1) return;
        this.fieldOrder.update((arr) => {
            const next = [...arr];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next;
        });
    }

    selecionarTodosCampos(): void {
        this.fieldOrder.set([...ALL_FIELDS]);
        this.selectedIds.set(new Set(ALL_FIELDS.map((f) => f.id)));
    }

    limparCampos(): void {
        this.selectedIds.set(new Set());
    }

    // ── Ações ─────────────────────────────────────────────────────────────────
    reiniciar(): void {
        this.selectedPrestadores.set([]);
        this.pdfBlobs.set([]);
        this.selecionarTodosCampos();
        this.jobService.pararPolling();
        this.jobService.setJob(null);
        this.jobService.limparSessionStorage();
    }

    private buildLoteRequest(): RelatorioSelecaoPjLoteExportRequest {
        const prestadores: PrestadorRefRequest[] = this.selectedPrestadores().map((p) => ({ nuCrm: p.nuCrm, sgUf: p.sgUf }));
        return {
            prestadores,
            mostraCnpj: this.hasField('cnpj'),
            mostraNome: this.hasField('nome'),
            mostraSituacao: this.hasField('situacao'),
            mostraClassificacao: this.hasField('classificacao'),
            mostraDiretoria: this.hasField('diretoria'),
            mostraCorpoSocietario: this.hasField('corpoSocietario'),
            mostraServicos: this.hasField('servicos'),
            mostraEnderecos: this.hasField('enderecos'),
            emissor: this.usuarioNome() || 'SICOM'
        };
    }

    /** Dispara a geração de um único PDF combinando todos os prestadores selecionados (formato de tabela do legado). */
    processarRelatorio(): void {
        if (this.selectedPrestadores().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um prestador para o relatório.' });
            return;
        }
        if (this.selectedFields().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um campo para o relatório.' });
            return;
        }

        this.pdfBlobs.set([]);
        this.isLoading.set(true);
        this.jobService.setJob({ jobId: '', status: 'PENDING', progress: 0 });

        this.relatorioService
            .exportarPdfLote(this.buildLoteRequest())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resposta) => {
                    if (resposta.status === 200) {
                        const body = resposta.body as RelatorioSelecaoPjExportResponse;
                        const hasPdf = !!body?.pdf;
                        if (hasPdf) {
                            this.pdfBlobs.update((list) => [...list, this.base64ToBlob(body.pdf!)]);
                        }
                        const msg = body?.message?.trim();
                        const foiIgnorado = !!msg && msg.includes('não encontrados');
                        this.messageService.add({
                            severity: hasPdf && !foiIgnorado ? 'success' : 'warn',
                            summary: hasPdf && !foiIgnorado ? 'Sucesso' : 'Atenção',
                            detail: msg || 'Relatório gerado.'
                        });
                    } else if (resposta.status === 202) {
                        const jobResp = resposta.body as RelatorioSelecaoPjJobCreatedResponse;
                        this.jobService.setJob({ jobId: jobResp.jobId, status: 'PENDING', progress: 0 });
                        this.messageService.add({
                            severity: 'info',
                            summary: 'Em processamento',
                            detail: 'Relatório sendo gerado em background. Você será notificado quando estiver pronto.',
                            life: 5000
                        });
                        this.jobService.iniciarPolling(jobResp.jobId);
                    }
                },
                error: (err: unknown) => {
                    this.isLoading.set(false);
                    const response = (err as { error?: RelatorioSelecaoPjExportResponse })?.error;
                    if (response?.pdf) {
                        this.pdfBlobs.update((list) => [...list, this.base64ToBlob(response.pdf!)]);
                        return;
                    }
                    if (response?.message?.trim()) {
                        this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: response.message!.trim() });
                        return;
                    }
                    this.jobService.updateStatus('ERROR');
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao gerar o arquivo PDF.' });
                },
                complete: () => {
                    this.isLoading.set(false);
                    if (!this.jobService.job?.jobId) {
                        this.jobService.setJob(null);
                    }
                }
            });
    }

    /** Gera CSV client-side a partir dos prestadores na fila e dos campos selecionados. */
    exportarCsv(): void {
        if (this.selectedPrestadores().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um prestador para exportar.' });
            return;
        }
        if (this.selectedFields().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um campo para exportar.' });
            return;
        }

        const fieldMap: Record<string, (p: PrestadorListItem) => string> = {
            cnpj: (p) => p.nuCgcPj,
            nome: (p) => `${p.nmPrestador} (CRM ${p.nuCrm}/${p.sgUf})`,
            situacao: (p) => p.dsSituacao,
            classificacao: (p) => p.dsClassificacao ?? ''
        };

        const visibleFields = this.selectedFields().filter((f) => f.id in fieldMap);
        const header = visibleFields.map((f) => f.label).join(';');
        const rows = this.selectedPrestadores().map((p) => visibleFields.map((f) => `"${(fieldMap[f.id](p) ?? '').replace(/"/g, '""')}"`).join(';'));
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio-selecao-prestadores-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }

    private base64ToBlob(base64: string): Blob {
        const normalized = base64.includes(',') ? base64.split(',')[1] : base64;
        const byteString = atob(normalized);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i += 1) {
            byteArray[i] = byteString.charCodeAt(i);
        }
        return new Blob([byteArray], { type: 'application/pdf' });
    }
}
